import { useEffect, useMemo } from 'react'

import { DashboardLayout } from "features/common/ui/DashboardLayout"
import { SkeletonBoard } from 'features/common/ui/SkeletonBoard'
import { filesPreviewActions } from 'features/files-preview/model/slice/filesPreviewSlice'
import {
  fetchFilesPublicLinkInfoThunk,
  FilesPublicLinksErrorTypes
} from 'features/public-link-files/model/slice/publicLinkFilesSlice'
import {
  getFilesPublicLinkErrorSelector,
  getFilesPublicLinkNeedAuthInPublicLinkSelector,
  getFilesPublicLinkNodeDataSelector,
} from 'features/public-link-files/selectors/publicLinkFilesSelectors'
import { isAuthSelector, userSelector } from 'features/user/model/selectors/userSelectors'
import { AuthModal } from 'features/user/ui/AuthModal'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { parsePathFromParams } from 'shared/hooks/parsePathFromParams'
import styled from 'styled-components'
import { FilesUploadingWidget } from 'features/files-uploading/ui/FilesUploadingWidget'
import { useGlobalDropzone } from 'features/dropzone/useGlobalDropzone'

import { ErrorComponent } from '../../features/public-link-files/ui/ErrorComponent'

import { PublicFilesPage } from './PublicFilesPage'

export const WrappedPublicFilesPage = () => {
  const dispatch = useAppDispatch()
  const { getRootProps } = useGlobalDropzone()

  const isAuth = useAppSelector(isAuthSelector)
  const user = useAppSelector(userSelector)
  const nodeData = useAppSelector(getFilesPublicLinkNodeDataSelector)
  const error = useAppSelector(getFilesPublicLinkErrorSelector)
  const needAuthInPublicLink = useAppSelector(getFilesPublicLinkNeedAuthInPublicLinkSelector)
  const params = useParams()
  const paths = useMemo(() => parsePathFromParams(params, 'fs'), [params])
  const shareId = useMemo(() => paths[0], [paths])

  useEffect(() => {
    if (shareId) {
      dispatch(fetchFilesPublicLinkInfoThunk({ shareId }))
    }
  }, [dispatch, shareId, user?.id])

  useEffect(() => {
    dispatch(filesPreviewActions.setIsPublicLink(true))

    return () => {
      dispatch(filesPreviewActions.setIsPublicLink(false))
    }
  }, [dispatch])

  if (error && !nodeData) {
    return (
      <ErrorComponent errorType={error as FilesPublicLinksErrorTypes} />
    )
  }

  if (!nodeData) {
    return (
      <>
        <SBoardPage>
          <SkeletonBoard />
        </SBoardPage>

        {needAuthInPublicLink && (
          <AuthModal />
        )}
      </>
    )
  }

  if (isAuth) {
    return (
      <DashboardLayout
        isPublicLink={true}
      >
        <PublicFilesPage isAuthenticatedView={true} />
      </DashboardLayout>
    )
  } else {
    return (
      <SDropzoneContainer
        {...getRootProps()}
      >
        <PublicFilesPage isAuthenticatedView={false} />

        <FilesUploadingWidget />
      </SDropzoneContainer>
    )
  }
}

const SBoardPage = styled.div`
  padding: 24px;
  position: relative;
`

const SDropzoneContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
`
